import { useDrawerStore } from "@/stores/useDrawerStore";
import { useVehiclesTableStore } from "@/stores/useTableStore";

import { ROUTES } from "../../../shared";
import { SETTINGS_CONTENT_NAMES } from "../../Navbar/types/enums";
import { STEP_GROUP } from "../types";

import useTutorialMode from "./useTutorialMode";

const useTutorialStartActions = (
  isMobile: boolean,
  pageLocation: ROUTES,
  setSettingsContent: (newContent: SETTINGS_CONTENT_NAMES) => void,
  closeMenu: () => void
) => {
  const isVehiclesPage = pageLocation === ROUTES.Vehicles;
  const {
    isSettingsOpen,
    clearTableHeadIfApplySchedule,
    handleClearVehiclesTableHead,
    setIsSettingsOpen,
    setTutorialDetails
  } = useTutorialMode();

  const setIsMobileDrawerOpen = useDrawerStore(
    state => state.setIsMobileDrawerOpen
  );
  const tableRowData = useVehiclesTableStore(state => state.data);
  const resetTableDetails = useVehiclesTableStore(
    state => state.resetTableDetails
  );

  const startCompanyCardTutorialMobile = () => {
    const stepIndex = pageLocation === ROUTES.Companies ? 2 : 0;
    setIsMobileDrawerOpen(false);
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex,
      stepGroup: STEP_GROUP.TutorialNewCompanyCard,
      isTutorialActive: true
    });
  };

  const startCompanyCardTutorialDesktop = () => {
    setSettingsContent(SETTINGS_CONTENT_NAMES.Initial);
    if (!isSettingsOpen) setIsSettingsOpen(true);
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: 0,
      stepGroup: STEP_GROUP.TutorialNewCompanyCard,
      isTutorialActive: true
    });
  };

  const startCompanySelectorTutorialMobile = () => {
    setIsMobileDrawerOpen(false);
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: 0,
      stepGroup: STEP_GROUP.TutorialCompanySelector,
      isTutorialActive: true
    });
  };

  const startCompanySelectorTutorialDesktop = () => {
    setSettingsContent(SETTINGS_CONTENT_NAMES.Tutorials);
    if (isSettingsOpen) setIsSettingsOpen(false);
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: 0,
      stepGroup: STEP_GROUP.TutorialCompanySelector,
      isTutorialActive: true
    });
  };

  const startCreateScheduleTutorialMobile = () => {
    setIsMobileDrawerOpen(false);
    if (isVehiclesPage) resetTableDetails();
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: 0,
      stepGroup: STEP_GROUP.TutorialCreateSchedule,
      isTutorialActive: true
    });
  };

  const startCreateScheduleTutorialDesktop = () => {
    closeMenu();
    if (isVehiclesPage) resetTableDetails();
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: 0,
      stepGroup: STEP_GROUP.TutorialCreateSchedule,
      isTutorialActive: true
    });
  };

  const hasVehicles = tableRowData.length > 0;

  const resolveApplyScheduleGroup = () =>
    isVehiclesPage && !hasVehicles
      ? STEP_GROUP.TutorialAddVehicle
      : STEP_GROUP.TutorialApplySchedule;

  const prepareApplyScheduleState = () => {
    if (!isVehiclesPage) return;
    clearTableHeadIfApplySchedule();
    handleClearVehiclesTableHead();
    resetTableDetails();
  };

  const startApplyScheduleTutorialMobile = () => {
    prepareApplyScheduleState();
    setIsMobileDrawerOpen(false);
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: isVehiclesPage && hasVehicles ? 2 : 0,
      stepGroup: resolveApplyScheduleGroup(),
      isTutorialActive: true
    });
  };

  const startApplyScheduleTutorialDesktop = () => {
    prepareApplyScheduleState();
    closeMenu();
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: isVehiclesPage && hasVehicles ? 1 : 0,
      stepGroup: resolveApplyScheduleGroup(),
      isTutorialActive: true
    });
  };

  const startCompanyCardTutorial = isMobile
    ? startCompanyCardTutorialMobile
    : startCompanyCardTutorialDesktop;

  const startCompanySelectorTutorial = isMobile
    ? startCompanySelectorTutorialMobile
    : startCompanySelectorTutorialDesktop;

  const startCreateScheduleTutorial = isMobile
    ? startCreateScheduleTutorialMobile
    : startCreateScheduleTutorialDesktop;

  const startApplyScheduleTutorial = isMobile
    ? startApplyScheduleTutorialMobile
    : startApplyScheduleTutorialDesktop;

  return {
    startCompanyCardTutorial,
    startCompanySelectorTutorial,
    startCreateScheduleTutorial,
    startApplyScheduleTutorial
  };
};

export default useTutorialStartActions;
